/**
 * Hypehub %30 campaign — state switching and countdowns.
 *
 * Start/end are absolute timestamps rendered from theme settings (window.HH_SALE), so every
 * visitor counts down to the same moment; nothing is stored per visitor and nothing restarts
 * on reload, navigation or a return visit. The browser clock is corrected with the server's
 * Date header, so a wrong device clock does not shift the countdown.
 *
 * <html data-hh-sale="upcoming|live|ended"> drives what CSS shows (prices, banner, strip).
 * Prices only ever change visually here; the amount charged comes from Shopify's automatic
 * discount, which uses the same start/end.
 */

const sale = window.HH_SALE;
let offset = 0;

/** @returns {number} Server-corrected "now" in ms. */
const now = () => Date.now() + offset;

/** @param {number} t */
function stateAt(t) {
  if (!sale) return 'off';
  if (sale.preview) return sale.preview; // merchant preview on an unpublished theme (see layout)
  if (t < sale.start) return 'upcoming';
  if (t < sale.end) return 'live';
  return 'ended';
}

const pad = (n) => String(n).padStart(2, '0');

/** @param {HTMLElement} el @param {number} ms */
function renderCountdown(el, ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const parts = {
    d: Math.floor(total / 86400),
    h: Math.floor((total % 86400) / 3600),
    m: Math.floor((total % 3600) / 60),
    s: total % 60,
  };
  for (const [unit, value] of Object.entries(parts)) {
    const node = el.querySelector(`[data-u="${unit}"]`);
    const text = unit === 'd' ? String(value) : pad(value);
    if (node && node.textContent !== text) node.textContent = text;
  }
  // Hide the day unit once it reaches zero (keeps the strip short on the last day).
  el.toggleAttribute('data-no-days', parts.d === 0);
}

let current = document.documentElement.getAttribute('data-hh-sale');

function tick() {
  const t = now();
  const next = stateAt(t);
  if (next !== current) {
    const previous = current;
    current = next;
    document.documentElement.setAttribute('data-hh-sale', next);
    document.dispatchEvent(new CustomEvent('hh:sale-state', { detail: { state: next, previous } }));
    // The cart page shows Shopify's real discount; re-render it when the campaign starts/ends.
    if (previous && location.pathname.replace(/\/$/, '').endsWith('/cart')) {
      setTimeout(() => location.reload(), 1500);
    }
  }
  for (const el of document.querySelectorAll('[data-hh-countdown]')) {
    const target = el.getAttribute('data-hh-countdown') === 'start' ? sale.start : sale.end;
    renderCountdown(/** @type {HTMLElement} */ (el), target - t);
  }
}

async function syncClock() {
  try {
    const started = Date.now();
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, { method: 'HEAD', cache: 'no-store' });
    const header = response.headers.get('Date');
    if (!header) return;
    const server = Date.parse(header);
    const latency = (Date.now() - started) / 2;
    const diff = server + latency - Date.now();
    // Date has 1 s resolution; only correct meaningful drift.
    if (Number.isFinite(diff) && Math.abs(diff) > 2000) {
      offset = diff;
      tick();
    }
  } catch {
    /* keep the device clock */
  }
}

function showPreviewBadge() {
  if (!sale?.preview || document.querySelector('.hh-preview-badge')) return;
  const names = { upcoming: 'başlamadan önce', live: 'kampanya sırasında', ended: 'kampanya bittikten sonra' };
  const badge = document.createElement('a');
  badge.className = 'hh-preview-badge';
  badge.href = `${location.pathname}?hh_preview=off`;
  badge.textContent = `Önizleme: ${names[sale.preview] ?? sale.preview} · kapat`;
  document.body.append(badge);
}

if (sale) {
  showPreviewBadge();
  tick();
  setInterval(tick, 1000);
  syncClock();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tick();
  });
}
